import { useMemo } from 'react';
import * as THREE from 'three';
import CalcHexFromRal from '../function/CalcHexFromRAL';

function Drive({ inputs, convRatio, drawingBaseDimensions }) {
  const motorColourRaw = inputs?.motor_colour;
  const motorColourStr =
    typeof motorColourRaw === 'number'
      ? String(motorColourRaw)
      : typeof motorColourRaw === 'string'
        ? motorColourRaw.trim()
        : '';
  let normalizedMotorRal = motorColourStr;
  if (/^\d{4}$/.test(motorColourStr)) {
    normalizedMotorRal = `RAL ${motorColourStr}`;
  } else if (/^RAL\s?\d{4}$/i.test(motorColourStr)) {
    normalizedMotorRal = motorColourStr.replace(
      /^RAL\s?(\d{4})$/i,
      'RAL $1000'
    );
  }
  const motorColor = CalcHexFromRal(
    normalizedMotorRal || 'RAL 1000'
  ).background;
  const gearBoxOA = (inputs.drive.dim_oa ?? 220) * convRatio;
  const gearBoxQ = (inputs.drive.dim_q ?? 220) * convRatio;
  const motorLength = inputs.drive.dim_c_lb * convRatio - gearBoxQ;
  const motorRadius = (inputs.drive.dim_eh / 2) * convRatio;

  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;
  const configAng = THREE.MathUtils.degToRad(
    reverseConfig === 1
      ? drawingBaseDimensions.configuration_angle
      : 360 - drawingBaseDimensions.configuration_angle
  );

  const width = convRatio * drawingBaseDimensions.belt_width;
  const thickness = convRatio * drawingBaseDimensions.winding_thickness;

  const inclination =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_center) *
    reverseConfig;
  const usedInclination =
    drawingBaseDimensions.u2_value > thickness
      ? 0
      : drawingBaseDimensions.ue_double_bend_value > thickness
        ? inclination / 2
        : inclination;

  const radiusLength = convRatio * (drawingBaseDimensions.central_diam / 2);
  const UELength =
    drawingBaseDimensions.u1 * convRatio * reverseConfig -
    (thickness / 2) * reverseConfig +
    Math.sin(usedInclination) * thickness;

  const baseX =
    Math.sin(Math.PI * 2 - configAng) * UELength +
    Math.cos(configAng) * radiusLength;
  const baseZ =
    Math.cos(Math.PI * 2 - configAng) * UELength +
    Math.sin(configAng) * radiusLength;

  const lateralOffset = gearBoxOA / 2 + width / 2 + 80 * convRatio;
  const sideSign = inputs?.motor_mounting_position === 'column' ? -1 : 1;
  const offsetX = sideSign * lateralOffset * Math.cos(configAng);
  const offsetZ = sideSign * lateralOffset * Math.sin(configAng);

  const driveX = baseX + offsetX;
  const driveY = drawingBaseDimensions.h3 * convRatio - thickness / 2;
  const driveZ = baseZ + offsetZ;

  const motorGeom = useMemo(
    () => new THREE.CylinderGeometry(motorRadius, motorRadius, motorLength, 32),
    [motorRadius, motorLength]
  );
  const shaftGeom = useMemo(
    () =>
      new THREE.CylinderGeometry(
        30 * convRatio,
        30 * convRatio,
        120 * convRatio,
        24
      ),
    [convRatio]
  );
  const gearBoxGeom = useMemo(
    () => new THREE.BoxGeometry(gearBoxOA, gearBoxOA, gearBoxQ),
    [gearBoxOA, gearBoxQ]
  );

  const yaw = Math.PI * 2 - configAng + (reverseConfig === 1 ? 0 : Math.PI);

  const mounting = (inputs?.motor_mounting_orientation || '').toLowerCase();
  let orientRot = [0, 0, 0];
  const reverseColumn = inputs?.motor_mounting_position === 'column' ? -1 : 1;
  switch (mounting) {
    case 'horizontally back':
      orientRot = [
        inputs?.motor_mounting_position === 'column' ? 0 : Math.PI,
        0,
        0,
      ];
      break;
    case 'vertically up':
      orientRot = [(-Math.PI / 2) * reverseColumn, 0, 0];
      break;
    case 'vertically down':
      orientRot = [(Math.PI / 2) * reverseColumn, 0, 0];
      break;
    case 'horizontally front':
    default:
      orientRot = [
        inputs?.motor_mounting_position === 'column' ? Math.PI : 0,
        0,
        0,
      ];
  }

  return (
    <group position={[driveX, driveY, driveZ]} rotation={[0, yaw, 0]}>
      <group rotation={orientRot}>
        {/* Gearbox block */}
        <mesh
          geometry={gearBoxGeom}
          position={[
            0,
            gearBoxOA / 2 - inputs.drive.dim_sa * convRatio,
            (gearBoxQ / 2 - inputs.drive.dim_qb * convRatio) * reverseColumn,
          ]}
        >
          <meshStandardMaterial
            color={motorColor}
            roughness={0.7}
            metalness={0.4}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Shaft protruding */}
        <mesh
          geometry={shaftGeom}
          rotation={[Math.PI / 2, 0, Math.PI / 2]}
          position={[
            (-gearBoxOA / 2 - (120 * convRatio) / 2) *
              reverseColumn *
              reverseConfig,
            0,
            0,
          ]}
        >
          <meshStandardMaterial
            color={motorColor}
            roughness={0.6}
            metalness={0.7}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Motor body attached on the gearbox side. If at column side, mirror to the other Z face */}
        <mesh
          geometry={motorGeom}
          rotation={[0, Math.PI / 2, Math.PI / 2]}
          position={[
            0,
            0,
            reverseColumn *
              (gearBoxQ - inputs.drive.dim_qb * convRatio + motorLength / 2),
          ]}
        >
          <meshStandardMaterial
            color={motorColor}
            roughness={0.7}
            metalness={0.5}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>
    </group>
  );
}

export default Drive;
